// src/pages/CalendarPage.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import {
  addDays,
  eachDayOfInterval,
  format,
  parse,
  startOfWeek,
  getDay,
  startOfMonth,
  endOfMonth,
  endOfWeek,
  isToday,
} from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  CalendarDays,
  CarFront,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Filter,
  LayoutGrid,
  Plus,
  RefreshCw,
  Wrench,
  X,
} from 'lucide-react';
import { createAuditedClient } from '../utils/auditClient';
import { fetchAuthSession } from 'aws-amplify/auth';
import AlertOverlay from '../components/AlertOverlay';
import TasksRail from '../components/calendar/TasksRail';
import ReservationDrawer from '../components/ReservationDrawer';
import { useCountry } from '../context/CountryContext';
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { getReservationCarSegments } from '../utils/reservationCarSegments';

const locales = { 'en-US': require('date-fns/locale/en-US') };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
});

const client = createAuditedClient('CalendarPage');

/* ================= GraphQL ================= */
// Lists
const ListCars = /* GraphQL */ `
  query ListCars {
    listCars(limit: 1000) {
      items { id name plate CompanyId active category }
      nextToken
    }
  }
`;
const ListCompanies = /* GraphQL */ `
  query ListCompanies {
    listCompanies(limit: 1000) {
      items {
        id
        name
        country
        currencies
        pickupLocations
        dropoffLocations
      }
      nextToken
    }
  }
`;

const GetCompany = /* GraphQL */ `
  query GetCompany($id: ID!) {
    getCompany(id: $id) {
      id
      name
      country
      currencies
      pickupLocations
      dropoffLocations
    }
  }
`;

const BaseReservationFields = `
  id
  CompanyId
  carId
  originalCarId
  originalCarName
  originalCarPlate
  carSubstitutions
  status
  title
  start
  end
  pickupAt
  pickupLocation
  dropoffAt
  dropoffLocation
  reservationDate
  reservationRef
  customerName
  customerEmail
  customerPhone
  flightNumber
  phoneNumbers
  babyCarSeat
  depositAmount
  rentalAmount
  depositCurrency
  rentalCurrency
  platform
  senderEmail
  acceptUrl
  declineUrl
  notes
  approvalStatus
  pickupChecklistStatus
  dropoffChecklistStatus
  extras
  paymentStatus 
  amountPaid          
  createdAt
  updatedAt
`;

const BaseMaintenanceFields = `
  id
  CompanyId
  carId
  title
  start
  end
  notes
  approvalStatus
  createdAt
  updatedAt
`;

// Reservation queries
const ByCompanyByStart_DT = /* GraphQL */ `
  query ByCompanyByStart($CompanyId: String!, $start: ModelStringKeyConditionInput) {
    reservationsByCompanyByStart(CompanyId: $CompanyId, start: $start, limit: 1000) {
      items { ${BaseReservationFields} }
      nextToken
    }
  }
`;
const ByCompanyByStart_STR = /* GraphQL */ `
  query ByCompanyByStart($CompanyId: String!, $start: ModelStringKeyConditionInput) {
    reservationsByCompanyByStart(CompanyId: $CompanyId, start: $start, limit: 1000) {
      items { ${BaseReservationFields} }
      nextToken
    }
  }
`;

// Maintenance queries
const MaintByCompanyByStart_DT = /* GraphQL */ `
  query MaintByCompanyByStart($CompanyId: String!, $start: ModelStringKeyConditionInput) {
    maintenanceByCompanyByStart(CompanyId: $CompanyId, start: $start, limit: 1000) {
      items { ${BaseMaintenanceFields} }
      nextToken
    }
  }
`;
const MaintByCompanyByStart_STR = /* GraphQL */ `
  query MaintByCompanyByStart($CompanyId: String!, $start: ModelStringKeyConditionInput) {
    maintenanceByCompanyByStart(CompanyId: $CompanyId, start: $start, limit: 1000) {
      items { ${BaseMaintenanceFields} }
      nextToken
    }
  }
`;

const UpdateReservation = /* GraphQL */ `
  mutation UpdateReservation($input: UpdateReservationInput!) {
    updateReservation(input: $input) { id }
  }
`;

/* =============== Helpers =============== */
function tzForCompanyCountry(country) {
  const c = String(country || '').trim().toUpperCase();

  // accept both ISO codes + common names
  if (c === 'GE' || c === 'GEO' || c === 'GEORGIA') return 'Asia/Tbilisi';
  if (c === 'AL' || c === 'ALB' || c === 'ALBANIA') return 'Europe/Tirane';
  if (c === 'LB' || c === 'LBN' || c === 'LEBANON') return 'Asia/Beirut';

  // do NOT use viewer tz as fallback
  return 'UTC';
}


// Parse AWSDateTime into a UTC Date (instant).
function parseAwsDateTimeToUtc(value, tz = 'UTC') {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const s = String(value).trim();
  if (!s) return null;

  const hasTZ = /([zZ]|[+-]\d{2}:\d{2})$/.test(s);

  let d;
  if (hasTZ) d = new Date(s);
  else {
    // Legacy fallback: no timezone in stored value => interpret as wall-clock in company tz.
    d = fromZonedTime(s, tz || 'UTC');
  }

  return Number.isNaN(d.getTime()) ? null : d;
}

// Convert an instant (UTC Date) into a "company wall-clock Date" for UI/RBC
function toCompanyZonedDate(utcDate, tz) {
  if (!utcDate) return null;
  return toZonedTime(utcDate, tz);
}

// Convert a "company wall-clock Date" back into a UTC instant (for querying)
function companyZonedToUtc(zonedDate, tz) {
  if (!zonedDate) return null;
  return fromZonedTime(zonedDate, tz);
}

function keyForRange(rangeZoned, tz) {
  const endUtc = companyZonedToUtc(rangeZoned.end, tz);
  return { le: endUtc.toISOString() };
}

function monthRangeFor(dateZoned, tz) {
  // dateZoned is already in company wall-clock space
  const startZoned = startOfWeek(startOfMonth(dateZoned));
  const endZoned = endOfWeek(endOfMonth(dateZoned));
  return { start: startZoned, end: endZoned };
}
function greenVariantForId(id) {
  // Fallback if something is weird
  if (!id) {
    return { bg: '#22c55e', border: '#16a34a' };
  }

  // Tiny deterministic hash based on the event id
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }

  const variants = [
    { bg: '#16a34a', border: '#166534' }, // slightly darker
    { bg: '#22c55e', border: '#16a34a' }, // your original-ish
    { bg: '#15803d', border: '#14532d' }, // darker
    { bg: '#1db954', border: '#15803d' }, // slightly brighter
  ];

  const idx = Math.abs(hash) % variants.length;
  return variants[idx];
}


function makeEvent(r, title, kind, tz, segment = null) {
  const startUtc = parseAwsDateTimeToUtc(segment?.start || r.start, tz);
  const endUtc = parseAwsDateTimeToUtc(segment?.end || r.end, tz);

  const start = toCompanyZonedDate(startUtc, tz);
  const end = toCompanyZonedDate(endUtc, tz);

  return {
    id: `${kind}:${r.id}:${segment?.segmentIndex ?? 0}`,
    title: title || '-',
    start,
    end,
    resourceId: segment?.carId || r.carId,
    resource: { ...r, __kind: kind, __segment: segment || null },
  };
}

function buildReservationEvents(reservations, selectedCarId, carMap, tz) {
  const events = [];

  for (const reservation of reservations || []) {
    const segments = getReservationCarSegments(reservation);
    for (const segment of segments) {
      if (selectedCarId && segment.carId !== selectedCarId) continue;
      const label = segment.carName || carMap.get(segment.carId) || '-';
      const cleanLabel = segment.carName || carMap.get(segment.carId) || '-';
      events.push(makeEvent(reservation, label || cleanLabel, 'res', tz, segment));
    }
  }

  return events;
}


function getApprovalStatus(row) {
  return (row?.approvalStatus || 'APPROVED').toUpperCase();
}
function isBlocking(row) {
  const st = getApprovalStatus(row);
  return st === 'APPROVED' || st === 'PENDING';
}

function sanitizeJson(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Number.isNaN(value)) return null;
  if (value instanceof Date) return value.toISOString();

  if (Array.isArray(value)) return value.map(sanitizeJson);

  if (value && typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) {
      const v = sanitizeJson(value[k]);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }
  return value;
}

function formatDurationCompact(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return '0m';

  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${minutes}m`;
}

/** simple width hook so we can not destroy mobile layouts */
function useIsNarrow(breakpoint = 900) {
  const [narrow, setNarrow] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth <= breakpoint
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setNarrow(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);

  return narrow;
}

/* =============== Page =============== */
const CALENDAR_CSS = `
  .adm-cal-page,
  .adm-cal-page * {
    box-sizing: border-box;
  }

  .adm-cal-page {
    --adm-bg: #f4f6f8;
    --adm-surface: #ffffff;
    --adm-surface-2: #f8fafc;
    --adm-line: #dfe5ec;
    --adm-line-strong: #cbd5e1;
    --adm-text: #101828;
    --adm-muted: #667085;
    --adm-soft: #eef2f6;
    --adm-ink: #0f172a;
    --adm-accent: #83d466;
    --adm-accent-dark: #2f6b22;
    --adm-blue: #2563eb;
    min-height: calc(100vh - 64px);
    background: var(--adm-bg);
    color: var(--adm-text);
  }

  .adm-cal-shell {
    width: 100%;
    max-width: 1480px;
    margin: 0 auto;
    padding: 22px 18px 28px;
    display: grid;
    gap: 16px;
  }

  .adm-page-header,
  .adm-filterbar,
  .adm-calendar-panel,
  .adm-rail-panel,
  .adm-fleet-panel,
  .adm-mobile-schedule {
    background: var(--adm-surface);
    border: 1px solid var(--adm-line);
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  .adm-page-header {
    padding: 18px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  .adm-title-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    color: var(--adm-muted);
    font-size: 12px;
    font-weight: 750;
    text-transform: uppercase;
    letter-spacing: 0;
  }

  .adm-page-header h1 {
    margin: 0;
    font-size: 26px;
    line-height: 1.15;
    letter-spacing: 0;
  }

  .adm-page-header p {
    max-width: 680px;
    margin: 6px 0 0;
    color: var(--adm-muted);
    font-size: 13px;
    line-height: 1.5;
  }

  .adm-header-actions,
  .adm-filter-actions,
  .adm-rbc-nav,
  .adm-rbc-actions,
  .adm-mobile-nav {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .adm-button {
    min-height: 38px;
    border: 1px solid var(--adm-line-strong);
    border-radius: 7px;
    padding: 8px 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: #ffffff;
    color: #111827;
    font-size: 13px;
    font-weight: 750;
    cursor: pointer;
    white-space: nowrap;
  }

  .adm-button:hover {
    background: #f8fafc;
  }

  .adm-button-primary {
    border-color: #5fad48;
    background: var(--adm-accent);
    color: #0f1f0c;
  }

  .adm-button-primary:hover {
    background: #75c45b;
  }

  .adm-icon-button {
    width: 36px;
    min-width: 36px;
    height: 36px;
    padding: 0;
  }

  .adm-filterbar {
    padding: 14px;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 14px;
    align-items: end;
  }

  .adm-filter-label {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--adm-muted);
    font-size: 12px;
    font-weight: 800;
  }

  .adm-filter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 10px;
    min-width: 0;
  }

  .adm-field {
    display: grid;
    gap: 5px;
    min-width: 0;
    color: #344054;
    font-size: 12px;
    font-weight: 750;
  }

  .adm-input {
    width: 100%;
    min-width: 0;
    height: 38px;
    border: 1px solid var(--adm-line-strong);
    border-radius: 7px;
    background: #ffffff;
    color: #111827;
    padding: 0 10px;
    font-size: 13px;
    outline: none;
  }

  .adm-input:focus {
    border-color: #7cbf68;
    box-shadow: 0 0 0 3px rgba(131, 212, 102, 0.22);
  }

  .adm-dashboard-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(300px, 360px);
    gap: 16px;
    align-items: start;
  }

  .adm-main-stack,
  .adm-side-stack {
    min-width: 0;
    display: grid;
    gap: 16px;
  }

  .adm-side-stack {
    position: sticky;
    top: 16px;
  }

  .adm-section-head {
    padding: 14px 14px 12px;
    border-bottom: 1px solid var(--adm-line);
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .adm-section-title {
    min-width: 0;
    display: grid;
    gap: 3px;
  }

  .adm-section-title h2,
  .adm-section-title h3 {
    margin: 0;
    color: var(--adm-text);
    font-size: 15px;
    line-height: 1.2;
  }

  .adm-section-title span {
    color: var(--adm-muted);
    font-size: 12px;
    line-height: 1.4;
  }

  .adm-cal-wrap {
    min-width: 0;
    overflow: hidden;
  }

  .adm-cal-page .rbc-calendar {
    width: 100%;
    max-width: 100%;
    background: #ffffff;
  }

  .adm-cal-page .rbc-month-view,
  .adm-cal-page .rbc-time-view {
    border: 0;
  }

  .adm-cal-page .rbc-month-row + .rbc-month-row {
    border-top: 1px solid var(--adm-line);
  }

  .adm-cal-page .rbc-day-bg + .rbc-day-bg {
    border-left: 1px solid var(--adm-line);
  }

  .adm-cal-page .rbc-header {
    padding: 9px 6px;
    border-bottom: 1px solid var(--adm-line);
    background: #f8fafc;
    color: #475467;
    font-size: 12px;
    font-weight: 800;
  }

  .adm-cal-page .rbc-date-cell {
    padding: 7px 7px 2px;
    color: #475467;
    font-size: 12px;
  }

  .adm-cal-page .rbc-off-range-bg {
    background: #fbfcfd;
  }

  .adm-cal-page .rbc-today {
    background: #f0fdf4;
  }

  .adm-cal-page .rbc-show-more {
    color: #1f2937;
    font-size: 12px;
    font-weight: 800;
  }

  .adm-cal-page .rbc-event {
    border-radius: 6px !important;
    padding: 2px 6px !important;
    box-shadow: none;
  }

  .adm-cal-page .rbc-event:focus {
    outline: 2px solid rgba(37, 99, 235, 0.28);
    outline-offset: 1px;
  }

  .adm-cal-ev {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .adm-cal-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    flex: 0 0 auto;
    background: rgba(255, 255, 255, 0.94);
  }

  .adm-cal-ev span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    font-weight: 750;
    line-height: 1.2;
  }

  .adm-rbc-toolbar {
    min-height: 58px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--adm-line);
    background: #ffffff;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 12px;
    align-items: center;
  }

  .adm-rbc-title {
    min-width: 0;
    text-align: center;
    display: grid;
    gap: 2px;
  }

  .adm-rbc-title strong {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--adm-text);
    font-size: 14px;
    line-height: 1.25;
  }

  .adm-rbc-title span {
    color: var(--adm-muted);
    font-size: 12px;
  }

  .adm-seg {
    display: inline-flex;
    padding: 3px;
    border: 1px solid var(--adm-line-strong);
    border-radius: 7px;
    background: #f8fafc;
  }

  .adm-seg button {
    min-height: 30px;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: #475467;
    padding: 5px 10px;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
  }

  .adm-seg button:hover {
    background: #ffffff;
  }

  .adm-seg button[data-active="true"] {
    background: var(--adm-ink);
    color: #ffffff;
  }

  .adm-cal-page .rbc-agenda-view table.rbc-agenda-table {
    border: 0;
  }

  .adm-cal-page .rbc-agenda-view .rbc-agenda-date-cell,
  .adm-cal-page .rbc-agenda-view .rbc-agenda-time-cell,
  .adm-cal-page .rbc-agenda-view .rbc-agenda-event-cell {
    padding: 12px;
    border-bottom: 1px solid var(--adm-line);
    font-size: 12px;
  }

  .adm-mobile-schedule {
    display: grid;
    overflow: hidden;
  }

  .adm-mobile-schedule-head {
    padding: 14px;
    border-bottom: 1px solid var(--adm-line);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .adm-mobile-schedule-head h2 {
    margin: 2px 0 0;
    font-size: 18px;
    line-height: 1.2;
  }

  .adm-mobile-schedule-head span {
    color: var(--adm-muted);
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0;
  }

  .adm-day-strip {
    padding: 10px 12px;
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 58px;
    gap: 8px;
    overflow-x: auto;
    border-bottom: 1px solid var(--adm-line);
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-x: contain;
  }

  .adm-day-chip {
    height: 70px;
    border: 1px solid var(--adm-line);
    border-radius: 8px;
    background: #ffffff;
    color: #111827;
    display: grid;
    align-content: center;
    justify-items: center;
    gap: 4px;
    cursor: pointer;
  }

  .adm-day-chip[data-active="true"] {
    border-color: var(--adm-ink);
    background: var(--adm-ink);
    color: #ffffff;
  }

  .adm-day-chip[data-today="true"]:not([data-active="true"]) {
    border-color: #5fad48;
    box-shadow: inset 0 0 0 1px rgba(95, 173, 72, 0.28);
  }

  .adm-day-chip-weekday {
    font-size: 11px;
    font-weight: 850;
    text-transform: uppercase;
  }

  .adm-day-chip-number {
    font-size: 20px;
    font-weight: 900;
    line-height: 1;
  }

  .adm-day-chip-count {
    min-height: 15px;
    padding: 1px 6px;
    border-radius: 999px;
    background: #eef2f6;
    color: #475467;
    font-size: 10px;
    font-weight: 850;
  }

  .adm-day-chip[data-active="true"] .adm-day-chip-count {
    background: rgba(255, 255, 255, 0.16);
    color: #ffffff;
  }

  .adm-schedule-list {
    padding: 12px;
    display: grid;
    gap: 10px;
  }

  .adm-schedule-heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    color: var(--adm-muted);
    font-size: 12px;
    font-weight: 800;
  }

  .adm-event-card {
    width: 100%;
    min-width: 0;
    border: 1px solid var(--adm-line);
    border-left: 4px solid #16a34a;
    border-radius: 8px;
    padding: 11px;
    background: #ffffff;
    color: inherit;
    text-align: left;
    cursor: pointer;
    display: grid;
    gap: 8px;
  }

  .adm-event-card:hover {
    background: #f8fafc;
  }

  .adm-event-card-maint {
    border-left-color: #111827;
  }

  .adm-event-card-pending {
    border-left-color: #d97706;
  }

  .adm-event-main {
    min-width: 0;
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: flex-start;
  }

  .adm-event-title {
    min-width: 0;
    display: grid;
    gap: 3px;
  }

  .adm-event-title strong {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    color: var(--adm-text);
  }

  .adm-event-title span {
    color: var(--adm-muted);
    font-size: 12px;
    line-height: 1.35;
  }

  .adm-event-time {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #344054;
    font-size: 12px;
    font-weight: 850;
    white-space: nowrap;
  }

  .adm-event-tags {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  .adm-tag {
    min-height: 22px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 7px;
    border-radius: 999px;
    background: #eef2f6;
    color: #344054;
    font-size: 11px;
    font-weight: 800;
  }

  .adm-tag-maint {
    background: #111827;
    color: #ffffff;
  }

  .adm-tag-pending {
    background: #fef3c7;
    color: #92400e;
  }

  .adm-empty-state {
    border: 1px dashed var(--adm-line-strong);
    border-radius: 8px;
    padding: 18px;
    color: var(--adm-muted);
    background: #fbfcfd;
    font-size: 13px;
    line-height: 1.45;
  }

  .adm-fleet-panel {
    overflow: hidden;
  }

  .adm-fleet-summary {
    padding: 16px;
    display: grid;
    gap: 14px;
    background: #101828;
    color: #ffffff;
  }

  .adm-fleet-summary-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
  }

  .adm-fleet-summary h2,
  .adm-fleet-progress h3 {
    margin: 0;
    font-size: 15px;
    line-height: 1.2;
  }

  .adm-fleet-summary p,
  .adm-fleet-progress p {
    margin: 4px 0 0;
    color: rgba(255, 255, 255, 0.72);
    font-size: 12px;
    line-height: 1.45;
  }

  .adm-fleet-pill {
    flex: 0 0 auto;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 999px;
    padding: 7px 10px;
    background: rgba(255, 255, 255, 0.1);
    font-size: 12px;
    font-weight: 850;
  }

  .adm-fleet-body {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(240px, 0.8fr);
    gap: 12px;
  }

  .adm-util-meter {
    display: grid;
    gap: 10px;
  }

  .adm-util-number {
    font-size: 38px;
    line-height: 1;
    font-weight: 950;
  }

  .adm-meter-track {
    height: 12px;
    border-radius: 999px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.16);
  }

  .adm-meter-fill {
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #83d466 0%, #22c55e 100%);
  }

  .adm-meter-meta {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    color: rgba(255, 255, 255, 0.76);
    font-size: 12px;
  }

  .adm-mini-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .adm-mini-stat {
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    padding: 10px;
    background: rgba(255, 255, 255, 0.08);
    display: grid;
    gap: 4px;
  }

  .adm-mini-stat span {
    color: rgba(255, 255, 255, 0.72);
    font-size: 11px;
    font-weight: 800;
  }

  .adm-mini-stat strong {
    color: #ffffff;
    font-size: 23px;
    line-height: 1;
  }

  .adm-fleet-progress {
    padding: 16px;
    display: grid;
    gap: 12px;
    background: #ffffff;
  }

  .adm-rental-list {
    display: grid;
    gap: 10px;
    max-height: 330px;
    overflow-y: auto;
  }

  .adm-rental-card {
    border: 1px solid var(--adm-line);
    border-radius: 8px;
    padding: 12px;
    display: grid;
    gap: 8px;
    background: #ffffff;
  }

  .adm-rental-head {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: flex-start;
  }

  .adm-rental-title {
    min-width: 0;
    display: grid;
    gap: 3px;
  }

  .adm-rental-title strong {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
  }

  .adm-rental-title span,
  .adm-rental-times,
  .adm-rental-duration {
    color: var(--adm-muted);
    font-size: 11px;
  }

  .adm-rental-badge {
    flex: 0 0 auto;
    border-radius: 999px;
    padding: 5px 8px;
    background: var(--adm-ink);
    color: #ffffff;
    font-size: 11px;
    font-weight: 850;
  }

  .adm-rental-times,
  .adm-rental-duration {
    display: flex;
    justify-content: space-between;
    gap: 10px;
  }

  .adm-day-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 9998;
    background: rgba(15, 23, 42, 0.34);
    padding: 18px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
  }

  .adm-day-modal {
    width: min(680px, 100%);
    max-height: min(80vh, 760px);
    overflow-y: auto;
    border: 1px solid var(--adm-line);
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  }

  .adm-day-modal-head {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 14px;
    border-bottom: 1px solid var(--adm-line);
    background: #ffffff;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .adm-day-modal-head h2 {
    margin: 0;
    font-size: 17px;
  }

  .adm-day-modal-list {
    padding: 12px;
    display: grid;
    gap: 10px;
  }

  @media (max-width: 1100px) {
    .adm-dashboard-grid {
      grid-template-columns: minmax(0, 1fr);
    }

    .adm-side-stack {
      position: static;
    }
  }

  @media (max-width: 760px) {
    .adm-cal-page {
      min-height: calc(100vh - 56px);
    }

    .adm-cal-shell {
      padding: 12px;
      gap: 12px;
    }

    .adm-page-header,
    .adm-filterbar {
      padding: 14px;
    }

    .adm-page-header {
      display: grid;
    }

    .adm-page-header h1 {
      font-size: 22px;
    }

    .adm-header-actions,
    .adm-filter-actions {
      width: 100%;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .adm-button {
      width: 100%;
      min-width: 0;
      padding-left: 10px;
      padding-right: 10px;
    }

    .adm-filterbar {
      grid-template-columns: minmax(0, 1fr);
      align-items: stretch;
    }

    .adm-filter-grid,
    .adm-fleet-body,
    .adm-mini-stats {
      grid-template-columns: minmax(0, 1fr);
    }

    .adm-side-stack {
      order: -1;
    }

    .adm-rental-list {
      max-height: none;
      overflow: visible;
    }

    .adm-fleet-summary-head,
    .adm-rental-head,
    .adm-event-main {
      display: grid;
    }

    .adm-fleet-pill,
    .adm-rental-badge,
    .adm-event-time {
      justify-self: start;
    }

    .adm-day-modal-backdrop {
      align-items: flex-end;
      padding: 0;
    }

    .adm-day-modal {
      width: 100%;
      max-height: 86vh;
      border-radius: 12px 12px 0 0;
      border-left: 0;
      border-right: 0;
      border-bottom: 0;
    }
  }
`;

function safeFormatDate(date, pattern, fallback = '') {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return fallback;
  try {
    return format(date, pattern);
  } catch {
    return fallback;
  }
}

function dateKey(date) {
  return safeFormatDate(date, 'yyyy-MM-dd');
}

function isMaintenanceEvent(event) {
  return event?.resource?.__kind === 'maint';
}

function getEventApproval(event) {
  return String(event?.resource?.approvalStatus || 'APPROVED').toUpperCase();
}

function getEventCardClass(event) {
  if (isMaintenanceEvent(event)) return 'adm-event-card adm-event-card-maint';
  if (getEventApproval(event) === 'PENDING') return 'adm-event-card adm-event-card-pending';
  return 'adm-event-card';
}

function getEventTimeLabel(event) {
  const start = event?.start;
  const end = event?.end;
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return '';
  if (!(end instanceof Date) || Number.isNaN(end.getTime())) return safeFormatDate(start, 'MMM d, HH:mm');

  if (dateKey(start) === dateKey(end)) {
    return `${safeFormatDate(start, 'HH:mm')} - ${safeFormatDate(end, 'HH:mm')}`;
  }

  return `${safeFormatDate(start, 'MMM d, HH:mm')} - ${safeFormatDate(end, 'MMM d, HH:mm')}`;
}

function getEventSummary(event, carMap) {
  const r = event?.resource || {};
  const maint = isMaintenanceEvent(event);
  const kind = maint ? 'Maintenance' : 'Reservation';
  const status = String(r.status || (maint ? 'MAINT' : 'RES')).toUpperCase();
  const car = event?.title || (r.carId ? carMap?.get?.(r.carId) : '') || '-';
  const customer = r.customerName || '';
  const location = [r.pickupLocation, r.dropoffLocation].filter(Boolean).join(' to ');
  const meta = [
    customer,
    r.reservationRef ? `Ref ${r.reservationRef}` : '',
    r.platform || '',
    location,
  ].filter(Boolean);

  return {
    car,
    kind,
    status,
    meta,
    approval: getEventApproval(event),
    maint,
  };
}

function eventOverlapsDay(event, day) {
  const start = event?.start;
  const end = event?.end || start;
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return false;
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(day);
  dayEnd.setHours(23, 59, 59, 999);
  return start <= dayEnd && end >= dayStart;
}

function buildVisibleDays(range) {
  const start = range?.start instanceof Date ? range.start : new Date();
  const end = range?.end instanceof Date ? range.end : addDays(start, 13);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return [new Date()];
  }
  try {
    return eachDayOfInterval({ start, end });
  } catch {
    return [start];
  }
}

function CalendarEvent({ event }) {
  return (
    <div className="adm-cal-ev" title={event?.title || ''}>
      <div className="adm-cal-dot" />
      <span>{event?.title || '-'}</span>
    </div>
  );
}

function CalendarToolbar(props) {
  const { label, onNavigate, onView, view } = props;

  return (
    <div className="adm-rbc-toolbar">
      <div className="adm-rbc-nav" aria-label="Navigate calendar">
        <button
          type="button"
          className="adm-button adm-icon-button"
          onClick={() => onNavigate('PREV')}
          aria-label="Previous period"
          title="Previous period"
        >
          <ChevronLeft size={17} />
        </button>
        <button
          type="button"
          className="adm-button"
          onClick={() => onNavigate('TODAY')}
        >
          Today
        </button>
        <button
          type="button"
          className="adm-button adm-icon-button"
          onClick={() => onNavigate('NEXT')}
          aria-label="Next period"
          title="Next period"
        >
          <ChevronRight size={17} />
        </button>
      </div>

      <div className="adm-rbc-title">
        <strong>{label}</strong>
        <span>{view === 'agenda' ? 'Two-week agenda' : 'Fleet month board'}</span>
      </div>

      <div className="adm-rbc-actions">
        <div className="adm-seg" role="group" aria-label="Calendar view">
          <button
            type="button"
            data-active={view === 'month'}
            onClick={() => onView('month')}
          >
            Month
          </button>
          <button
            type="button"
            data-active={view === 'agenda'}
            onClick={() => onView('agenda')}
          >
            Agenda
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleEventCard({ event, carMap, onOpen }) {
  const summary = getEventSummary(event, carMap);
  const Icon = summary.maint ? Wrench : CarFront;

  return (
    <button
      type="button"
      className={getEventCardClass(event)}
      onClick={() => onOpen?.(event)}
    >
      <div className="adm-event-main">
        <div className="adm-event-title">
          <strong>{summary.car}</strong>
          <span>{summary.meta.length ? summary.meta.join(' / ') : summary.kind}</span>
        </div>
        <span className="adm-event-time">
          <Clock3 size={14} />
          {getEventTimeLabel(event)}
        </span>
      </div>

      <div className="adm-event-tags">
        <span className={`adm-tag ${summary.maint ? 'adm-tag-maint' : ''}`}>
          <Icon size={13} />
          {summary.kind}
        </span>
        <span className={summary.approval === 'PENDING' ? 'adm-tag adm-tag-pending' : 'adm-tag'}>
          {summary.approval === 'PENDING' ? 'Pending' : summary.status}
        </span>
      </div>
    </button>
  );
}

function MobileSchedulePanel({
  events,
  visibleRange,
  currentDate,
  carMap,
  onOpen,
  onNavigatePeriod,
}) {
  const dayStripRef = useRef(null);
  const days = useMemo(() => buildVisibleDays(visibleRange), [visibleRange]);
  const currentKey = dateKey(currentDate) || dateKey(days[0]);
  const [selectedKey, setSelectedKey] = useState(currentKey);

  const eventCounts = useMemo(() => {
    const counts = new Map();
    for (const day of days) {
      counts.set(dateKey(day), events.filter((event) => eventOverlapsDay(event, day)).length);
    }
    return counts;
  }, [days, events]);

  useEffect(() => {
    const dayKeys = new Set(days.map(dateKey));
    setSelectedKey((previousKey) => {
      if (dayKeys.has(previousKey)) return previousKey;
      if (dayKeys.has(currentKey)) return currentKey;
      return dateKey(days[0]);
    });
  }, [currentKey, days]);

  useEffect(() => {
    const strip = dayStripRef.current;
    if (!strip || !selectedKey) return undefined;

    const frame = window.requestAnimationFrame(() => {
      const activeDay = strip.querySelector(`[data-day-key="${selectedKey}"]`);
      if (!activeDay) return;

      const targetLeft =
        activeDay.offsetLeft - (strip.clientWidth - activeDay.clientWidth) / 2;
      const maxLeft = Math.max(0, strip.scrollWidth - strip.clientWidth);

      strip.scrollTo({
        left: Math.max(0, Math.min(targetLeft, maxLeft)),
        behavior: 'smooth',
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [selectedKey, days.length]);

  const selectedDay = useMemo(
    () => days.find((day) => dateKey(day) === selectedKey) || days[0],
    [days, selectedKey]
  );

  const selectedEvents = useMemo(
    () =>
      events
        .filter((event) => eventOverlapsDay(event, selectedDay))
        .slice()
        .sort((a, b) => (a.start?.getTime?.() || 0) - (b.start?.getTime?.() || 0)),
    [events, selectedDay]
  );

  return (
    <section className="adm-mobile-schedule">
      <div className="adm-mobile-schedule-head">
        <div>
          <span>Mobile schedule</span>
          <h2>{safeFormatDate(currentDate, 'MMMM yyyy', 'Schedule')}</h2>
        </div>
        <div className="adm-mobile-nav">
          <button
            type="button"
            className="adm-button adm-icon-button"
            onClick={() => onNavigatePeriod('PREV')}
            aria-label="Previous month"
            title="Previous month"
          >
            <ChevronLeft size={17} />
          </button>
          <button
            type="button"
            className="adm-button"
            onClick={() => onNavigatePeriod('TODAY')}
          >
            Today
          </button>
          <button
            type="button"
            className="adm-button adm-icon-button"
            onClick={() => onNavigatePeriod('NEXT')}
            aria-label="Next month"
            title="Next month"
          >
            <ChevronRight size={17} />
          </button>
        </div>
      </div>

      <div
        ref={dayStripRef}
        className="adm-day-strip"
        aria-label="Days in visible calendar range"
      >
        {days.map((day) => {
          const key = dateKey(day);
          const count = eventCounts.get(key) || 0;
          return (
            <button
              key={key}
              type="button"
              className="adm-day-chip"
              data-day-key={key}
              data-active={key === selectedKey}
              data-today={isToday(day)}
              onClick={() => setSelectedKey(key)}
              aria-label={`${safeFormatDate(day, 'EEEE, MMMM d')}, ${count} events`}
            >
              <span className="adm-day-chip-weekday">{safeFormatDate(day, 'EEE')}</span>
              <span className="adm-day-chip-number">{safeFormatDate(day, 'd')}</span>
              <span className="adm-day-chip-count">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="adm-schedule-list">
        <div className="adm-schedule-heading">
          <span>{safeFormatDate(selectedDay, 'EEEE, MMM d')}</span>
          <span>{selectedEvents.length} items</span>
        </div>

        {selectedEvents.length === 0 ? (
          <div className="adm-empty-state">No reservations or maintenance for this day.</div>
        ) : (
          selectedEvents.map((event) => (
            <ScheduleEventCard
              key={event.id}
              event={event}
              carMap={carMap}
              onOpen={onOpen}
            />
          ))
        )}
      </div>
    </section>
  );
}

/* =============== Page =============== */
export default function CalendarPage() {

  const [claims, setClaims] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const { selectedCompanyId: ctxCompanyId, loadingCompany } = useCountry();
  const isNarrow = useIsNarrow(600); // < 900px → mobile-ish

  const [companies, setCompanies] = useState([]);
  const [cars, setCars] = useState([]);
  const carMap = useMemo(
    () => new Map(cars.map((c) => [c.id, c.name + (c.plate ? ` (${c.plate})` : '')])),
    [cars]
  );

  const [editableCompanyIds, setEditableCompanyIds] = useState(new Set());
  const [drawerReadOnly, setDrawerReadOnly] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  const isAdmin = useMemo(
    () => !!(claims?.groups || []).includes('admin'),
    [claims]
  );
  const canView = useMemo(
    () =>
      (claims?.groups || []).some((g) =>
        ['admin', 'country_admin', 'editor', 'staff'].includes(g)
      ),
    [claims]
  );

  const canEditForCompany = useCallback(
    (companyId) => {
      const groups = claims?.groups || [];
      if (groups.includes('admin') || groups.includes('country_admin')) return true;
      if (!companyId) return false;
      return editableCompanyIds.has(companyId);
    },
    [claims?.groups, editableCompanyIds]
  );

  const activeCompanyId = useMemo(
    () => (isAdmin ? selectedCompanyId || claims?.CompanyId : claims?.CompanyId),
    [isAdmin, selectedCompanyId, claims?.CompanyId]
  );
  const activeCompany = useMemo(() => {
    if (!activeCompanyId) return null;
    return companies.find((c) => c.id === activeCompanyId) || null;
  }, [companies, activeCompanyId]);

  const activeTz = useMemo(() => {
    const tz = tzForCompanyCountry(activeCompany?.country);
    if (tz === 'UTC') console.warn('Unknown company.country:', activeCompany?.country, activeCompany);
    return tz;
  }, [activeCompany]);

  const [selectedCarId, setSelectedCarId] = useState('');
  const [events, setEvents] = useState([]);
  const [useStringKeyCond, setUseStringKeyCond] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create'); // 'create' | 'edit'
  const [drawerInitial, setDrawerInitial] = useState(null);

  const [visibleRange, setVisibleRange] = useState({
    start: new Date(),
    end: new Date(),
  });
  const lastRangeRef = useRef(null);

  const [view, setView] = useState('month');
  const [dayModal, setDayModal] = useState({ open: false, date: null, events: [] });
  const [currentDate, setCurrentDate] = useState(() =>
    toCompanyZonedDate(new Date(), activeTz)
  );
  const [calendarHeight, setCalendarHeight] = useState(isNarrow ? 680 : 700);
  useEffect(() => {
    if (isNarrow && view !== 'month') setView('month');
  }, [isNarrow, view]);
  // custom alert state
  const [alert, setAlert] = useState(null);
  const pushAlert = useCallback((payload) => {
    setAlert({
      id: Date.now(),
      type: payload.type || 'info',
      title: payload.title,
      message: payload.message,
      detail: payload.detail,
      confirmLabel: payload.confirmLabel,
      cancelLabel: payload.cancelLabel,
      onConfirm: payload.onConfirm,
      onCancel: payload.onCancel,
    });
  }, []);

  const isCompanyAdmin = useMemo(
    () => !!(claims?.groups || []).includes('country_admin'),
    [claims]
  );

  const canRemoveFromRail = useMemo(
    () => isAdmin || isCompanyAdmin,
    [isAdmin, isCompanyAdmin]
  );
  // Auth
  useEffect(() => {
    (async () => {
      const { tokens } = await fetchAuthSession();
      const p = tokens?.idToken?.payload || {};
      const CompanyId = p['custom:CompanyId'] ?? p['custom:companyId'] ?? null;
      const groups = p['cognito:groups'] || [];
      setClaims({ CompanyId, groups, sub: p.sub || null, email: p.email || null });
      setAuthReady(Boolean(CompanyId) || groups?.includes('admin'));
      if (!groups?.includes('admin') && CompanyId) setSelectedCompanyId(CompanyId);
    })();
  }, []);

  // Which companies this user can edit (Member.canEditCalendar)
  useEffect(() => {
    if (!claims?.sub) {
      setEditableCompanyIds(new Set());
      return;
    }

    const groups = claims?.groups || [];

    if (groups.includes('admin') || groups.includes('country_admin')) {
      setEditableCompanyIds(new Set());
      return;
    }

    (async () => {
      try {
        const res = await client.graphql({
          query: /* GraphQL */ `
          query MyMember {
            listMembers(
              limit: 1000
              filter: { sub: { eq: "${claims.sub}" } }
            ) {
              items { id CompanyId canEditCalendar }
            }
          }
        `,
          authMode: 'userPool',
        });
        const items = res?.data?.listMembers?.items || [];
        const allowed = new Set(
          items
            .filter((m) => !!m?.canEditCalendar)
            .map((m) => m.CompanyId)
        );
        setEditableCompanyIds(allowed);
      } catch (err) {
        console.warn('Member probe failed', err);
        setEditableCompanyIds(new Set());
      }
    })();
  }, [claims?.sub, claims?.groups]);

  // Lists
  const loadCars = useCallback(async () => {
    try {
      const res = await client.graphql({ query: ListCars, authMode: 'userPool' });
      const all = (res.data?.listCars?.items || []).filter(Boolean);
      setCars(all);
    } catch (e) {
      console.error('Cars load failed:', e);
      setCars([]);
      pushAlert &&
        pushAlert({
          type: 'error',
          title: 'Failed to load cars',
          message: 'Could not load cars. Check console for details.',
        });
    }
  }, [pushAlert]);

  const loadCompanies = useCallback(async () => {
    try {
      if (isAdmin) {
        const res = await client.graphql({ query: ListCompanies, authMode: 'userPool' });
        const all = (res.data?.listCompanies?.items || [])
          .filter(Boolean)
          .sort((a, b) => a.name.localeCompare(b.name));
        setCompanies(all);
      } else if (claims?.CompanyId) {
        const res = await client.graphql({
          query: GetCompany,
          variables: { id: claims.CompanyId },
          authMode: 'userPool',
        });
        const co = res.data?.getCompany;
        setCompanies(co ? [co] : []);
      } else {
        setCompanies([]);
      }
    } catch (e) {
      console.error('Companies load failed:', e);
      setCompanies([]);
      pushAlert &&
        pushAlert({
          type: 'error',
          title: 'Failed to load companies',
          message: 'Could not load companies. Check console for details.',
        });
    }
  }, [isAdmin, claims?.CompanyId, pushAlert]);

  // sync admin selected company with country context
  useEffect(() => {
    if (!authReady) return;
    if (!isAdmin) return;
    setSelectedCompanyId(ctxCompanyId || '');
  }, [authReady, isAdmin, ctxCompanyId]);

  useEffect(() => {
    if (authReady) {
      loadCars();
      loadCompanies();
    }
  }, [authReady, loadCars, loadCompanies]);

  const scopedCars = useMemo(() => {
    if (!authReady) return [];
    if (!activeCompanyId) {
      return cars.filter((c) => c.active);
    }
    return cars.filter((c) => c.active && c.CompanyId === activeCompanyId);
  }, [cars, authReady, activeCompanyId]);

  const resources = useMemo(
    () =>
      scopedCars.map((c) => ({
        id: c.id,
        title: c.name + (c.plate ? ` (${c.plate})` : ''),
      })),
    [scopedCars]
  );
  const calendarResources = useMemo(() => {
    if (isNarrow) return undefined;
    return resources;
  }, [isNarrow, resources]);
  const visibleFleetCars = useMemo(() => {
    if (!selectedCarId) return scopedCars;
    return scopedCars.filter((car) => car.id === selectedCarId);
  }, [scopedCars, selectedCarId]);

  const fleetUtilization = useMemo(() => {
    const now = toCompanyZonedDate(new Date(), activeTz);
    const nowMs = now.getTime();
    const visibleCarIds = new Set(visibleFleetCars.map((car) => car.id));
    const activeByCar = new Map();

    for (const event of events) {
      const resource = event?.resource || {};
      if (resource.__kind !== 'res') continue;

      const carId = event?.resourceId || resource?.carId;
      const startMs = event?.start instanceof Date ? event.start.getTime() : NaN;
      const endMs = event?.end instanceof Date ? event.end.getTime() : NaN;

      if (
        !carId ||
        !visibleCarIds.has(carId) ||
        !Number.isFinite(startMs) ||
        !Number.isFinite(endMs) ||
        endMs <= startMs ||
        nowMs < startMs ||
        nowMs >= endMs
      ) {
        continue;
      }

      const existing = activeByCar.get(carId);
      if (!existing || endMs < existing.endMs) {
        activeByCar.set(carId, {
          id: event.id,
          carId,
          startMs,
          endMs,
          label: event.title || carMap.get(carId) || '-',
          category: resource.category || '',
          customerName: resource.customerName || '',
          reservationRef: resource.reservationRef || '',
          platform: resource.platform || '',
        });
      }
    }

    const activeRentals = Array.from(activeByCar.values())
      .map((rental) => {
        const totalMs = rental.endMs - rental.startMs;
        const elapsedMs = Math.max(0, Math.min(nowMs - rental.startMs, totalMs));
        const remainingMs = Math.max(rental.endMs - nowMs, 0);
        const progressPct = Math.max(0, Math.min(100, Math.round((elapsedMs / Math.max(totalMs, 1)) * 100)));

        return {
          ...rental,
          start: new Date(rental.startMs),
          end: new Date(rental.endMs),
          elapsedMs,
          remainingMs,
          totalMs,
          progressPct,
        };
      })
      .sort((a, b) => {
        if (a.endMs !== b.endMs) return a.endMs - b.endMs;
        return a.label.localeCompare(b.label);
      });

    const totalCars = visibleFleetCars.length;
    const rentedNowCount = activeRentals.length;
    const availableNowCount = Math.max(totalCars - rentedNowCount, 0);
    const utilizationPct = totalCars > 0 ? Math.round((rentedNowCount / totalCars) * 100) : 0;
    const returningTodayCount = activeRentals.filter(
      (rental) => format(rental.end, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd')
    ).length;
    const averageProgressPct = activeRentals.length > 0
      ? Math.round(
        activeRentals.reduce((sum, rental) => sum + rental.progressPct, 0) / activeRentals.length
      )
      : 0;

    return {
      activeRentals,
      totalCars,
      rentedNowCount,
      availableNowCount,
      utilizationPct,
      returningTodayCount,
      averageProgressPct,
    };
  }, [events, visibleFleetCars, activeTz, carMap]);

  const loadForRange = useCallback(
    async (range) => {
      if (!authReady) return;
      if (!range) return;

      lastRangeRef.current = range;

      const startCond = keyForRange(range, activeTz);


      const tryOnce = async (forceString) => {
        if (!activeCompanyId) {
          return { reservations: [], maintenance: [] };
        }

        const reservationQuery = forceString ? ByCompanyByStart_STR : ByCompanyByStart_DT;
        const maintenanceQuery =
          forceString ? MaintByCompanyByStart_STR : MaintByCompanyByStart_DT;

        const [res, mt] = await Promise.all([
          client.graphql({
            query: reservationQuery,
            variables: { CompanyId: activeCompanyId, start: startCond },
            authMode: 'userPool',
          }),
          client.graphql({
            query: maintenanceQuery,
            variables: { CompanyId: activeCompanyId, start: startCond },
            authMode: 'userPool',
          }),
        ]);

        return {
          reservations: res.data?.reservationsByCompanyByStart?.items || [],
          maintenance: mt.data?.maintenanceByCompanyByStart?.items || [],
        };
      };

      try {
        const { reservations, maintenance } = await tryOnce(useStringKeyCond);

        const resBlocking = (reservations || []).filter(isBlocking);
        const mtBlocking = (maintenance || [])
          .filter(isBlocking)
          .filter((m) => !selectedCarId || m.carId === selectedCarId);

        const cleanedEvents = [
          ...buildReservationEvents(resBlocking, selectedCarId, carMap, activeTz),
          ...mtBlocking.map((m) =>
            makeEvent(m, `${carMap.get(m.carId) || '-'} - maintenance`, 'maint', activeTz)
          ),
        ];
        setEvents(cleanedEvents);
      } catch (err) {
        const msg = String(err?.errors?.[0]?.message || err?.message || '');

        if (!useStringKeyCond && /Unknown type .*ModelAWSDateTimeKeyConditionInput/i.test(msg)) {
          try {
            const { reservations, maintenance } = await tryOnce(true);

            const resBlocking = (reservations || []).filter(isBlocking);
            const mtBlocking = (maintenance || []).filter(isBlocking);

            const filteredMtBlocking = mtBlocking.filter(
              (m) => !selectedCarId || m.carId === selectedCarId
            );
            const cleanedEvents = [
              ...buildReservationEvents(resBlocking, selectedCarId, carMap, activeTz),
              ...filteredMtBlocking.map((m) =>
                makeEvent(m, `${carMap.get(m.carId) || '-'} - maintenance`, 'maint', activeTz)
              ),
            ];
            setEvents(cleanedEvents);
            setUseStringKeyCond(true);
          } catch (err2) {
            console.error('Calendar load error (fallback):', err2);
            setEvents([]);
            pushAlert &&
              pushAlert({
                type: 'error',
                title: 'Calendar load failed',
                message: 'Could not load calendar data. Check console for details.',
              });
          }
        } else {
          console.error('Calendar load error:', err);
          setEvents([]);
          pushAlert &&
            pushAlert({
              type: 'error',
              title: 'Calendar load failed',
              message: 'Could not load calendar data. Check console for details.',
            });
        }
      }
    },
    [
      authReady,
      activeCompanyId,
      activeTz,
      selectedCarId,
      useStringKeyCond,
      carMap,
      pushAlert,
    ]
  );

  const setCalendarPeriod = useCallback(
    (dateZoned, nextView = view) => {
      const safeDate =
        dateZoned instanceof Date && !Number.isNaN(dateZoned.getTime())
          ? dateZoned
          : toCompanyZonedDate(new Date(), activeTz);

      setCurrentDate(safeDate);

      const range =
        nextView === 'agenda'
          ? { start: safeDate, end: addDays(safeDate, 13) }
          : monthRangeFor(safeDate, activeTz);

      setVisibleRange(range);
      lastRangeRef.current = range;
      if (authReady) loadForRange(range);
    },
    [activeTz, authReady, loadForRange, view]
  );

  const handleCalendarNavigate = useCallback(
    (newDate, nextView) => {
      setCalendarPeriod(toCompanyZonedDate(newDate, activeTz), nextView || view);
    },
    [activeTz, setCalendarPeriod, view]
  );

  const handleMobilePeriodNavigate = useCallback(
    (action) => {
      if (action === 'TODAY') {
        setCalendarPeriod(toCompanyZonedDate(new Date(), activeTz), 'month');
        return;
      }

      const next = new Date(currentDate);
      if (Number.isNaN(next.getTime())) {
        setCalendarPeriod(toCompanyZonedDate(new Date(), activeTz), 'month');
        return;
      }

      next.setMonth(next.getMonth() + (action === 'PREV' ? -1 : 1));
      setCalendarPeriod(next, 'month');
    },
    [activeTz, currentDate, setCalendarPeriod]
  );

  useEffect(() => {
    if (!authReady) return;
    if (!activeCompanyId) return;

    const todayZoned = toCompanyZonedDate(new Date(), activeTz);
    const initialRange = monthRangeFor(todayZoned, activeTz);

    lastRangeRef.current = initialRange;
    setVisibleRange(initialRange);
    loadForRange(initialRange);
  }, [authReady, activeCompanyId, activeTz, loadForRange]);


  useEffect(() => {
    if (!authReady) return;
    if (lastRangeRef.current) loadForRange(lastRangeRef.current);
  }, [authReady, selectedCarId, selectedCompanyId, loadForRange]);
  // Fixed month height tuned for up to 5 visible events per day
  useEffect(() => {
    if (isNarrow) {
      setCalendarHeight(780);
      return;
    }

    if (view !== 'month') {
      setCalendarHeight(700);
      return;
    }

    const VISIBLE_ROWS_PER_DAY = 5;
    const HEADER_ROW = 26;
    const EVENT_ROW = 24;
    const rowHeight = HEADER_ROW + VISIBLE_ROWS_PER_DAY * EVENT_ROW;
    const PADDING = 60;
    setCalendarHeight(rowHeight * 6 + PADDING);
  }, [isNarrow, view]);


  const eventPropGetter = useCallback((event) => {
    const r = event?.resource || {};
    const kind = r.__kind;
    const approval = (r.approvalStatus || 'APPROVED').toUpperCase();

    const style = {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontWeight: 600,
      borderRadius: 6,
      padding: '2px 4px',
    };

    if (kind === 'maint') {
      style.background = '#111827';
      style.color = '#ffffff';
      style.border = '1px solid #111827';
      return { style };
    }

    if (approval === 'APPROVED') {
      const { bg, border } = greenVariantForId(event.id);
      style.background = bg;
      style.border = `1px solid ${border}`;
      style.color = '#ffffff';
    } else if (approval === 'PENDING') {
      style.background = '#f59e0b';
      style.border = '1px solid #d97706';
      style.color = '#111827';
    } else {
      style.background = '#e5e7eb';
      style.border = '1px solid #9ca3af';
      style.color = '#111827';
    }

    return { style };
  }, []);


  function onSelectEvent({ resource }) {
    if (!canView) return;
    const kind = resource?.__kind === 'maint' ? 'maint' : 'res';
    const allow = canEditForCompany(resource?.CompanyId);
    setDrawerMode('edit');
    setDrawerInitial({ ...resource, __kind: kind });
    setDrawerOpen(true);
    setDrawerReadOnly(!allow);
  }

  function onNewReservation() {
    const companyId = activeCompanyId;
    const allow = canEditForCompany(companyId);

    if (!allow) {
      pushAlert &&
        pushAlert({
          type: 'error',
          title: 'No edit permission',
          message: 'You do not have permission to create calendar items for this agency.',
        });
      return;
    }

    setDrawerMode('create');
    setDrawerInitial(null);
    setDrawerReadOnly(!allow);
    setDrawerOpen(true);
  }

  async function onSavedThenReload() {
    if (lastRangeRef.current) await loadForRange(lastRangeRef.current);
  }

  const handleRemoveChecklistTask = useCallback(
    (taskRow) => {
      if (!taskRow) return;
      const res = taskRow.ev?.resource;
      if (!res || res.__kind !== 'res') return;

      const companyId = res.CompanyId;
      if (!canEditForCompany(companyId)) return;
      if (!pushAlert) return;

      const isPickup = taskRow.kind === 'Pickup';
      const isDropoff = taskRow.kind === 'Dropoff';
      if (!isPickup && !isDropoff) return;

      pushAlert({
        type: 'confirm',
        title: 'Remove task from rail',
        message:
          'This will mark this handover as "not required" and hide it from the task rail. The reservation itself will stay.',
        confirmLabel: 'Mark as not required',
        cancelLabel: 'Cancel',
        onConfirm: async () => {
          try {
            const input = {
              id: res.id,
              ...(res._version != null ? { _version: res._version } : {}),
            };

            if (isPickup) {
              input.pickupChecklistStatus = 'NOT_REQUIRED';
            } else if (isDropoff) {
              input.dropoffChecklistStatus = 'NOT_REQUIRED';
            }

            await client.graphql({
              query: UpdateReservation,
              variables: { input: sanitizeJson(input) },
              authMode: 'userPool',
            });

            if (lastRangeRef.current) {
              await loadForRange(lastRangeRef.current);
            }
          } catch (e) {
            console.error('Remove checklist task failed:', e);
            pushAlert({
              type: 'error',
              title: 'Update failed',
              message:
                'Could not update the reservation. Check console for details.',
            });
          }
        },
      });
    },
    [canEditForCompany, pushAlert, loadForRange]
  );

  if (!authReady || loadingCompany) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ fontSize: 14, color: '#6b7280' }}>Loading calendar…</div>
      </div>
    );
  }

  if (isAdmin && !activeCompanyId) {
    return (
      <div style={{ padding: 24 }}>
        <b>No active company.</b>
        <p style={{ fontSize: 13, color: '#6b7280' }}>
          No company resolved for the current country. Check your Company.country values
          or the country toggle.
        </p>
      </div>
    );
  }

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <b>Not authorized.</b> You don’t have access to the calendar.
      </div>
    );
  }
  const canCreateActive = canEditForCompany(activeCompanyId);
  const activeAgencyLabel = activeCompany?.name || (isAdmin ? 'Selected agency' : 'Agency');
  const rangeLabel = visibleRange?.start && visibleRange?.end
    ? `${safeFormatDate(visibleRange.start, 'MMM d')} - ${safeFormatDate(visibleRange.end, 'MMM d')}`
    : 'Visible range';

  const calendarPanel = isNarrow ? (
    <MobileSchedulePanel
      events={events}
      visibleRange={visibleRange}
      currentDate={currentDate}
      carMap={carMap}
      onOpen={onSelectEvent}
      onNavigatePeriod={handleMobilePeriodNavigate}
    />
  ) : (
    <section className="adm-calendar-panel">
      <div className="adm-section-head">
        <div className="adm-section-title">
          <h2>Fleet board</h2>
          <span>{rangeLabel} / {activeAgencyLabel}</span>
        </div>
        <span className="adm-tag">
          <LayoutGrid size={13} />
          {view === 'agenda' ? 'Agenda' : 'Month'}
        </span>
      </div>
      <div className="adm-cal-wrap">
        <Calendar
          localizer={localizer}
          events={events}
          components={{
            toolbar: (p) => <CalendarToolbar {...p} isNarrow={isNarrow} />,
            event: CalendarEvent,
          }}
          startAccessor="start"
          endAccessor="end"
          selectable={false}
          style={{ height: calendarHeight }}
          eventPropGetter={eventPropGetter}
          onSelectEvent={onSelectEvent}
          date={currentDate}
          onNavigate={handleCalendarNavigate}
          popup
          popupOffset={{ x: 20, y: 10 }}
          onShowMore={(evts, date) => setDayModal({ open: true, date, events: evts })}
          views={['month', 'agenda']}
          view={view}
          onView={(nextView) => {
            setView(nextView);
            setCalendarPeriod(currentDate, nextView);
          }}
          length={14}
          resources={calendarResources}
          resourceIdAccessor="id"
          resourceTitleAccessor="title"
          scrollToTime={new Date(1970, 0, 1, 8, 0, 0)}
        />
      </div>
    </section>
  );

  const fleetSection = (
    <section className="adm-fleet-panel">
      <div className="adm-fleet-summary">
        <div className="adm-fleet-summary-head">
          <div>
            <h2>Fleet utilization</h2>
            <p>Active rentals right now, scoped to the current agency and car filter.</p>
          </div>
          <div className="adm-fleet-pill">
            {fleetUtilization.rentedNowCount}/{fleetUtilization.totalCars} rented now
          </div>
        </div>

        <div className="adm-fleet-body">
          <div className="adm-util-meter">
            <div className="adm-util-number">{fleetUtilization.utilizationPct}%</div>
            <div className="adm-meter-track">
              <div
                className="adm-meter-fill"
                style={{ width: `${fleetUtilization.utilizationPct}%`, maxWidth: '100%' }}
              />
            </div>
            <div className="adm-meter-meta">
              <span>{fleetUtilization.availableNowCount} available</span>
              <span>{fleetUtilization.returningTodayCount} returning today</span>
              <span>{selectedCarId ? 'Selected car' : 'Filtered fleet'}</span>
            </div>
          </div>

          <div className="adm-mini-stats">
            {[
              { label: 'Rented', value: fleetUtilization.rentedNowCount },
              { label: 'Available', value: fleetUtilization.availableNowCount },
              { label: 'Avg progress', value: `${fleetUtilization.averageProgressPct}%` },
            ].map((item) => (
              <div className="adm-mini-stat" key={item.label}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="adm-fleet-progress">
        <div className="adm-section-title">
          <h3>Active rental progress</h3>
          <span>Sorted by soonest return.</span>
        </div>

        {fleetUtilization.rentedNowCount === 0 ? (
          <div className="adm-empty-state">No cars are currently rented for the active filters.</div>
        ) : (
          <div className="adm-rental-list">
            {fleetUtilization.activeRentals.map((car) => {
              const metaParts = [
                car.customerName,
                car.reservationRef ? `Ref ${car.reservationRef}` : '',
                car.platform,
                car.category,
              ].filter(Boolean);

              return (
                <div className="adm-rental-card" key={car.id}>
                  <div className="adm-rental-head">
                    <div className="adm-rental-title">
                      <strong>{car.label}</strong>
                      <span>{metaParts.length > 0 ? metaParts.join(' / ') : 'Rental currently in progress'}</span>
                    </div>
                    <span className="adm-rental-badge">{car.progressPct}% elapsed</span>
                  </div>

                  <div className="adm-rental-times">
                    <span>{safeFormatDate(car.start, 'MMM d, HH:mm')}</span>
                    <span>{safeFormatDate(car.end, 'MMM d, HH:mm')}</span>
                  </div>

                  <div className="adm-meter-track" style={{ background: '#e2e8f0' }}>
                    <div
                      className="adm-meter-fill"
                      style={{
                        width: `${car.progressPct}%`,
                        maxWidth: '100%',
                        minWidth: car.progressPct > 0 ? 12 : 0,
                      }}
                    />
                  </div>

                  <div className="adm-rental-duration">
                    <span>{formatDurationCompact(car.elapsedMs)} elapsed</span>
                    <span>{formatDurationCompact(car.remainingMs)} left</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );

  const tasksRailSection = (
    <section className="adm-rail-panel">
      <TasksRail
        events={events}
        carMap={carMap}
        visibleStart={visibleRange.start}
        visibleEnd={visibleRange.end}
        onOpen={onSelectEvent}
        canRemoveAssignments={canRemoveFromRail}
        onRemoveAssignment={handleRemoveChecklistTask}
        tz={activeTz}
      />
    </section>
  );

  return (
    <div className="adm-cal-page">
      <style>{CALENDAR_CSS}</style>

      <div className="adm-cal-shell">
        <header className="adm-page-header">
          <div>
            <div className="adm-title-kicker">
              <CalendarDays size={15} />
              Admin calendar
            </div>
            <h1>Schedule command center</h1>
            <p>
              Reservations, maintenance, handover work, and live fleet occupancy for {activeAgencyLabel}.
            </p>
          </div>

          <div className="adm-header-actions">
            {canCreateActive && (
              <button type="button" className="adm-button adm-button-primary" onClick={onNewReservation}>
                <Plus size={16} />
                New reservation
              </button>
            )}
            <button
              type="button"
              className="adm-button"
              onClick={() => lastRangeRef.current && loadForRange(lastRangeRef.current)}
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </header>

        <section className="adm-filterbar">
          <div className="adm-filter-label">
            <Filter size={16} />
            Filters
          </div>

          <div className="adm-filter-grid">
            {isAdmin && (
              <label className="adm-field">
                Agency
                <select
                  className="adm-input"
                  value={selectedCompanyId}
                  onChange={(e) => {
                    setSelectedCompanyId(e.target.value);
                    setSelectedCarId('');
                  }}
                >
                  <option value="">All agencies</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </label>
            )}

            <label className="adm-field">
              Car
              <select
                className="adm-input"
                value={selectedCarId}
                onChange={(e) => setSelectedCarId(e.target.value)}
              >
                <option value="">All cars</option>
                {scopedCars.map((car) => (
                  <option key={car.id} value={car.id}>
                    {car.name}{car.plate ? ` (${car.plate})` : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="adm-filter-actions">
            <button
              type="button"
              className="adm-button"
              onClick={() => setCalendarPeriod(toCompanyZonedDate(new Date(), activeTz), 'month')}
            >
              Today
            </button>
            <button
              type="button"
              className="adm-button"
              onClick={() => {
                setSelectedCarId('');
                if (isAdmin) setSelectedCompanyId(ctxCompanyId || '');
              }}
            >
              Reset
            </button>
          </div>
        </section>

        <div className="adm-dashboard-grid">
          <main className="adm-main-stack">
            {calendarPanel}
            {fleetSection}
          </main>

          <aside className="adm-side-stack">
            {tasksRailSection}
          </aside>
        </div>
      </div>
      {/* Day modal */}
      {dayModal.open && (
        <div
          className="adm-day-modal-backdrop"
          onClick={() => setDayModal({ open: false, date: null, events: [] })}
        >
          <div
            className="adm-day-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="adm-day-modal-head">
              <div className="adm-section-title">
                <h2>{dayModal.date ? safeFormatDate(dayModal.date, 'EEEE, MMM d') : 'Day'}</h2>
                <span>{dayModal.events.length} scheduled items</span>
              </div>
              <button
                type="button"
                className="adm-button adm-icon-button"
                onClick={() => setDayModal({ open: false, date: null, events: [] })}
                aria-label="Close day details"
                title="Close"
              >
                <X size={17} />
              </button>
            </div>

            <div className="adm-day-modal-list">
              {dayModal.events
                .slice()
                .sort((a, b) => (a.start?.getTime?.() || 0) - (b.start?.getTime?.() || 0))
                .map((event) => (
                  <ScheduleEventCard
                    key={event.id}
                    event={event}
                    carMap={carMap}
                    onOpen={(selectedEvent) => {
                      onSelectEvent(selectedEvent);
                      setDayModal({ open: false, date: null, events: [] });
                    }}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
      <ReservationDrawer
        open={drawerOpen}
        mode={drawerMode}
        initial={drawerInitial}
        companies={isAdmin ? companies : companies.filter((c) => c.id === claims?.CompanyId)}
        cars={cars}
        claims={claims}
        useStringKeyCond={useStringKeyCond}
        activeCompanyId={activeCompanyId}
        readOnly={drawerReadOnly}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerInitial(null);
        }}
        onSaved={onSavedThenReload}
        pushAlert={pushAlert}
      />

      <AlertOverlay alert={alert} onClose={() => setAlert(null)} />
    </div>
  );
}


